import styles from './contract-change-list.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { GrayOutlinedButton, PrimaryButton } from '../../button/button';
import Card, { CardContent } from '../../card/card';
import { isNewLicense, SalePriceRenderer, calcContractItemRecurringPrice, calcNextInvoiceCost } from '../../helpers/helpers';
import { CreateHeaders, currencyHeader, HeaderSizeEnum, textHeader, toCurrency } from '../../table-helpers/table-helpers';
import Typography from '../../typography/typography';
import { useSingleContractDispatch } from '../contract-details-reducer/contract-details-reducer';
import { IContractInfo, ContractOrderTypesEnum, IOrderItem, Tooltip, DatePicker, FocusTable } from '@frontend-workspace/react-shared-lib';
import { t } from 'i18next';

/* eslint-disable-next-line */
export interface ContractChangeListProps {
  orderItems: IOrderItem[],
  contractInfo: IContractInfo,
  saleDate: Date,
  onNewSale: () => void
}

export function ContractChangeList(props: ContractChangeListProps) {
  const dispatch = useSingleContractDispatch();
  const handleSaleDateChange = (newDate: Date | null) => dispatch({ type: "SALE_ACTIVATION_DATE_CHANGE", newDate: newDate });

  const removeRenderer = (params: ValueFormatterParams) => {
    const handleDelete = () => dispatch({ type: "REMOVE_CHANGE", changeType: params.data.type, licenseNumber: params.data.licenseNumber, itemId: params.value })
    return <GrayOutlinedButton
      variant="outlined"
      size='small'
      onClick={handleDelete}>
      {t("standard.delete")}
    </GrayOutlinedButton>
  }

  const quantityRenderer = (params: ValueFormatterParams) => {
    const change: IOrderItem = params.data;
    if (change.type === ContractOrderTypesEnum.ChangeRecurringDiscountPercent) return ``;
    return params.value;
  }

  const priceRenderer = (params: ValueFormatterParams) => SalePriceRenderer(props.contractInfo, params.data, props.saleDate);
  const recurringPriceRenderer = (params: ValueFormatterParams) => toCurrency(calcContractItemRecurringPrice(params.data), props.contractInfo.currency);

  const changeRenderer = (params: ValueFormatterParams) => {
    const licenseNumber = isNewLicense(params.data.licenseNumber) ? "Ny" : params.data.licenseNumber;
    const productName: string = params.data.productName;
    const filteredName = productName.replace("Focus", "");
    return <Tooltip
      arrow
      title={licenseNumber}>
      {filteredName}
    </Tooltip>
  }

  const getNextInvoiceCost = () => props.orderItems.filter(o =>
    o.type !== ContractOrderTypesEnum.ChangeRecurringDiscountPercent &&
    o.type !== ContractOrderTypesEnum.TerminateLicense
  ).reduce((acc, item) => acc + calcNextInvoiceCost(props.contractInfo, item, item.oneTimeDiscountAmount, props.saleDate), 0);

  const headers = CreateHeaders([
    textHeader("", t("standard.license"), HeaderSizeEnum.EXTRA_LARGE, { renderer: changeRenderer }),
    textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.SMALL, {
      renderer: quantityRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("recurringPrice", t("standard.salePrice"), HeaderSizeEnum.MEDIUM, {
      renderer: priceRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("recurringPrice", t("standard.renewalPrice"), HeaderSizeEnum.MEDIUM, {
      renderer: recurringPriceRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("id", "", HeaderSizeEnum.SMALL, {
      renderer: removeRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return (
    <Card className='mb-4'>
      <CardContent>
        <Typography
          variant="h4"
          component="div"
          className="mb-4">
          {t("standard.newSale")}
        </Typography>
        <div className='mb-3'>
          <DatePicker
            inputFormat="dd.MM.yyyy"
            label={t("standard.executionDate")}
            value={props.saleDate}
            onChange={handleSaleDateChange}
          />
        </div>
        <FocusTable
          headers={headers}
          data={props.orderItems}
          disableSearch
          hidePagination
        />
        <div className='mt-2'>{t("standard.salesAmount")}: {toCurrency(getNextInvoiceCost(), props.contractInfo.currency)}</div>
        <PrimaryButton
          onClick={props.onNewSale}
          variant="contained"
          className="mt-3">
          {t("standard.goToApproval")}
        </PrimaryButton>
      </CardContent>
    </Card>
  );
}

export default ContractChangeList;